export type ColorRamp = 'teal' | 'blue' | 'coral' | 'purple' | 'amber';
export type PrayerTime = 'bomdod' | 'peshin' | 'asr' | 'shom' | 'xufton';
export type PrayerStatus = 'pending' | 'in_progress' | 'done' | 'skipped';
export type Role = 'child' | 'parent';

export const PRAYER_ORDER: readonly PrayerTime[] = [
  'bomdod',
  'peshin',
  'asr',
  'shom',
  'xufton',
] as const;

export const OPTIONAL_PRAYERS: readonly PrayerTime[] = ['shom', 'xufton'] as const;

export type ChildPublic = {
  id: number;
  name: string;
  color_ramp: ColorRamp;
  avatar_initial: string;
  avatar_url?: string | null;
};

export type ChildSession = ChildPublic & {
  daily_rule_text: string;
  current_surah_number: number;
  streak_days?: number;
};

export type SurahMeta = {
  number: number;
  name_ar: string;
  name_uz: string;
  ayah_count: number;
  start_page: number;
  juz: number;
};

export type PrayerRow = {
  prayer_time: PrayerTime;
  status: PrayerStatus;
  recitation_count: number;
  is_optional: boolean;
  target: number;
};

export type MemSlotStatus = 'done' | 'in_progress' | 'open' | 'upcoming';

export type MemSlot = {
  time: string; // 'HH:MM'
  target: number;
  count: number;
  status: MemSlotStatus;
  is_due: boolean;
  is_current: boolean;
};

export type MemorizationToday = {
  is_active: boolean;
  surah: { number: number; name_uz: string; name_ar: string };
  ayah_from: number;
  ayah_to: number;
  current_slot: string | null;
  slots: MemSlot[];
  total_count: number;
  total_target: number;
};

export type TodayResponse = {
  quran_day: string;
  mem_day: string;
  current_prayer: PrayerTime | null;
  prayers: PrayerRow[];
  recitation_active: boolean;
  current_surah: { number: number; name_uz: string; name_ar: string };
  daily_rule_text: string;
  target_per_prayer: number;
  memorization: MemorizationToday | null;
  streak_days: number;
  unseen_parent_messages: number;
  avatar_url?: string | null;
};

/** Carried via router state to tell the Recorder which module/slot it's recording for. */
export type RecordIntent =
  | { mode: 'recitation' }
  | { mode: 'memorization'; slot: string };

export type ModuleType = 'recitation' | 'memorization' | 'vocabulary';

export type RecitationConfig = { targets: Record<PrayerTime, number> };

export type MemorizationSlot = { time: string; target: number };

export type MemorizationConfig = {
  surah_number: number;
  ayah_from: number;
  ayah_to: number;
  slots: MemorizationSlot[];
};

export type AssignmentDTO =
  | { module_type: 'recitation'; is_active: boolean; config: RecitationConfig }
  | { module_type: 'memorization'; is_active: boolean; config: MemorizationConfig }
  | { module_type: 'vocabulary'; is_active: boolean; config: Record<string, unknown> };

export type ChildTodayRecitation = {
  id: number;
  uuid: string;
  prayer_time: PrayerTime;
  surah: { number: number; name_uz: string };
  duration_seconds: number;
  recorded_at: string;
  audio_url: string;
};

export type ChildMemRecording = {
  id: number;
  uuid: string;
  slot_key: string;
  surah: { number: number; name_uz: string };
  ayah_from: number;
  ayah_to: number;
  rep_index: number | null;
  duration_seconds: number;
  recorded_at: string;
  audio_url: string;
};

export type ChildProfileResponse = {
  child: {
    id: number;
    name: string;
    color_ramp: ColorRamp;
    avatar_initial: string;
    avatar_url?: string | null;
  };
  streak_days: number;
  totals: {
    recitations: number;
    duration_seconds: number;
    surahs_completed: number;
  };
  week: { recitations: number; days_active: number };
  month: { recitations: number };
  surahs_completed: Array<{
    surah_number: number;
    surah_name_uz: string;
    completed_on: string;
  }>;
};

export type ChildHistoryRecitation = {
  id: number;
  uuid: string;
  surah: { number: number; name_uz: string };
  duration_seconds: number;
  recorded_at: string;
  audio_url: string;
};

export type ChildHistoryDay = {
  quran_day: string;
  total_recitations: number;
  total_duration_seconds: number;
  prayers: Array<{
    prayer_time: PrayerTime;
    recitations: ChildHistoryRecitation[];
  }>;
};

export type ChildHistoryResponse = {
  days: ChildHistoryDay[];
  next_cursor: string | null;
};

export type ParentMessage = {
  id: number;
  message_type: 'text' | 'emoji' | 'quick_phrase';
  content: string;
  sent_at: string;
  seen_by_child: boolean;
};

export type FeedItem = {
  key: string; // `${module}:${id}` — unique across modules (numeric ids collide)
  id: number;
  module: 'recitation' | 'memorization';
  child: { id: number; name: string; color_ramp: ColorRamp };
  surah: { number: number; name_uz: string };
  prayer_time?: PrayerTime; // recitation
  slot_key?: string; // memorization ('HH:MM')
  ayah_from?: number; // memorization
  ayah_to?: number; // memorization
  rep_index?: number | null; // memorization
  duration_seconds: number;
  recorded_at: string;
  parent_heard: boolean;
  audio_url: string;
};

export type ParentOverview = {
  quran_day: string;
  totals: {
    recitations_today: number;
    recitations_all_time: number;
    unheard_count: number;
    longest_current_streak: number;
  };
  children: Array<{
    id: number;
    name: string;
    age: number;
    color_ramp: ColorRamp;
    avatar_initial: string;
    avatar_url?: string | null;
    current_surah: { number: number; name_uz: string };
    streak_days: number;
    target_per_prayer: number;
    today: {
      prayers: Array<{ prayer_time: PrayerTime; status: PrayerStatus; recitation_count: number }>;
      total_recitations: number;
    };
    memorization: { is_active: boolean; total_count: number; total_target: number } | null;
  }>;
};

export type ChildDetail = {
  child: {
    id: number;
    name: string;
    age: number;
    color_ramp: ColorRamp;
    avatar_initial: string;
    avatar_url?: string | null;
    daily_rule_text: string;
    current_surah_number: number;
    current_surah_name_uz: string;
    target_per_prayer: number;
    streak_days: number;
  };
  stats: {
    surahs_completed: Array<{ surah_number: number; completed_on: string }>;
    total_recitations: number;
    total_duration_seconds: number;
    streak_history: Array<{ start_date: string; end_date: string; length: number }>;
  };
  today: {
    prayers: PrayerRow[];
    total_recitations: number;
  };
  recent_prayer_completions: Array<{
    quran_day: string;
    prayer_time: PrayerTime;
    was_skipped: boolean;
    completed_at: string;
  }>;
};

export type WeekDayStatus = {
  quran_day: string;
  is_rest_day: boolean;
  prayers: Array<{ prayer_time: PrayerTime; status: 'done' | 'skipped' | 'in_progress' | 'not_started'; count: number }>;
  memorization: {
    count: number;
    total_target: number;
    status: 'done' | 'in_progress' | 'not_started';
  } | null;
};

export type WeekResponse = {
  days: string[];
  children: Array<{
    id: number;
    name: string;
    color_ramp: ColorRamp;
    daily_status: WeekDayStatus[];
  }>;
};

export type ChildSettingsRow = {
  id: number;
  name: string;
  age: number;
  color_ramp: ColorRamp;
  avatar_initial: string;
  avatar_url?: string | null;
  daily_rule_text: string;
  current_surah_number: number;
  target_per_prayer: number;
  pin_last_changed: string;
  assignments: AssignmentDTO[];
};

export type SettingsResponse = {
  children: ChildSettingsRow[];
  parent: { pin_last_changed: string };
};
